import { ArrowRight, History } from 'lucide-react'

const DATE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})
const MONEY_FORMATTER = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0,
})

function formatDate(value) {
  if (!value) return null

  return DATE_FORMATTER.format(new Date(value))
}

function formatMoney(cents) {
  if (cents === null || cents === undefined) return 'Unknown'

  return MONEY_FORMATTER.format(Number(cents) / 100)
}

function humanize(value) {
  if (!value) return 'Unknown'

  return String(value)
    .replaceAll('_', ' ')
    .replace(/^./, (character) => character.toUpperCase())
}

function eventPresentation(event) {
  if (event.eventType === 'price_reduced' || event.eventType === 'price_changed') {
    return {
      title: event.eventType === 'price_reduced' ? 'Price reduced' : 'Price changed',
      previous: formatMoney(event.previousValue?.price_cents),
      current: formatMoney(event.currentValue?.price_cents),
    }
  }

  if (event.eventType === 'status_changed') {
    return {
      title: 'Status changed',
      previous: humanize(event.previousValue?.status),
      current: humanize(event.currentValue?.status),
    }
  }

  return { title: 'Listing details changed', previous: null, current: null }
}

export function ListingHistory({ listingImport }) {
  const trackedSince = formatDate(listingImport?.trackedSince)
  if (!trackedSince) return null

  const history = listingImport.history ?? []

  return (
    <section className="space-y-4 rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-foreground">
          <History aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
          Price &amp; status history
        </h3>
        <p className="text-xs font-medium text-muted-foreground">
          Tracked by Sentinel since {trackedSince}
        </p>
      </div>

      {history.length ? (
        <ol className="divide-y divide-border rounded-lg border border-border">
          {history.map((event) => {
            const presentation = eventPresentation(event)

            return (
              <li key={`${event.eventType}-${event.observedAt}`} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-bold text-foreground">{presentation.title}</p>
                  {presentation.previous && presentation.current ? (
                    <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{presentation.previous}</span>
                      <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
                      <span className="font-medium text-foreground">{presentation.current}</span>
                    </p>
                  ) : null}
                </div>
                <time className="text-xs text-muted-foreground" dateTime={event.observedAt}>
                  {formatDate(event.observedAt)}
                </time>
              </li>
            )
          })}
        </ol>
      ) : (
        <p className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
          No price or status changes recorded yet.
        </p>
      )}
    </section>
  )
}
